import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { HandHeart, Sparkles, UserRoundSearch } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClientFilterPill } from '@/components/client-ui/ClientFilterPill';
import { ClientPickerSection } from '@/components/client-ui/ClientPickerSection';
import { ClientSearchInput } from '@/components/client-ui/ClientSearchInput';
import { ClientWizardLayout } from '@/components/client-ui/ClientWizardLayout';
import { ClientListCardSkeleton } from '@/components/client-ui/skeletons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  CONCERN_LABELS,
  LANGUAGE_KEYS,
  LANGUAGE_LABELS,
  PRACTITIONER_CONCERN_KEYS,
  PRACTITIONER_SPECIALIZATION_KEYS,
  SPECIALIZATION_LABELS,
  type Language,
  type PractitionerConcern,
  type PractitionerSpecialization,
} from '@/features/availability/constants/practitionerFilterEnums';
import type { BookingRosterEntry } from '@/features/availability/types';
import { DEFAULT_DURATION_MINUTES } from '@/features/booking/components/ClientDurationPicker';
import { ClientPractitionerCardLg } from '@/features/booking/components/ClientPractitionerCardLg';
import { PractitionerProfileDialog } from '@/features/booking/components/PractitionerProfileDialog';
import { useBookingRoster } from '@/features/booking/hooks/useBookingRoster';
import { nextAvailableDate } from '@/features/booking/lib/nextAvailableDate';
import { parseWizardSearch } from '@/features/booking/state/wizardSearch';
import { formatRelativeDayLabel } from '@/lib/dates';
import { matchesNameQuery, splitRecentPractitioners } from '@/lib/practitionerPicker';

export const Route = createFileRoute('/client/book/practitioner')({
  component: ClientBookPractitionerStep,
  validateSearch: (s: Record<string, unknown>) => parseWizardSearch(s),
});

/** Header "Session Mode" label derived from a practitioner's available slots. */
function sessionModeLabelFor(slots: readonly { mode: string }[]): string | null {
  const modes = new Set(slots.map((s) => s.mode));
  if (modes.size === 0) return null;
  if (modes.size === 1 && modes.has('online')) return 'Online';
  if (modes.size === 1 && modes.has('offline')) return 'Offline';
  return 'Online / Offline';
}

type PractitionerKind = 'psychologist' | 'volunteer';

/** One placeholder per column at the widest breakpoint, so the grid fills a
 *  row instead of leaving a ragged single card. */
const PRACTITIONER_SKELETON_COUNT = 3;

/** Section and search copy per kind. */
const KIND_COPY_KEYS: Record<
  PractitionerKind,
  { recent: string; other: string; searchPlaceholder: string }
> = {
  psychologist: {
    recent: 'wizard.step.practitioner.sectionRecentPsychologist',
    other: 'wizard.step.practitioner.sectionOtherPsychologist',
    searchPlaceholder: 'wizard.step.practitioner.searchPlaceholderPsychologist',
  },
  volunteer: {
    recent: 'wizard.step.practitioner.sectionRecentVolunteer',
    other: 'wizard.step.practitioner.sectionOtherVolunteer',
    searchPlaceholder: 'wizard.step.practitioner.searchPlaceholderVolunteer',
  },
};

/** A roster entry plus the first day it can be booked under the current mode
 *  filter; null when nothing is bookable this week. */
interface PickerRow {
  entry: BookingRosterEntry;
  nextDate: string | null;
}

function rosterName(entry: BookingRosterEntry): string {
  const p = entry.practitioner;
  if (p.displayName) return p.displayName;
  return [p.firstName, p.lastName].filter(Boolean).join(' ');
}

/** ANY-match, the same rule the server applied when these filters were
 *  query params. An empty filter matches everyone. */
function overlaps({
  stored,
  wanted,
}: {
  stored: readonly string[];
  wanted: readonly string[];
}): boolean {
  return wanted.length === 0 || wanted.some((w) => stored.includes(w));
}

/** Soonest opening first, then A to Z. No opening this week sinks to the end. */
function compareByNextOpening(a: PickerRow, b: PickerRow): number {
  if (a.nextDate !== b.nextDate) {
    if (a.nextDate === null) return 1;
    if (b.nextDate === null) return -1;
    return a.nextDate < b.nextDate ? -1 : 1;
  }
  return rosterName(a.entry).localeCompare(rosterName(b.entry));
}

function PractitionerKindToggle({
  value,
  onChange,
}: {
  value: PractitionerKind;
  onChange: (next: PractitionerKind) => void;
}) {
  const isListener = value === 'volunteer';
  return (
    <div
      role="radiogroup"
      aria-label="Practitioner kind"
      className="inline-flex items-center gap-3 px-4 py-2"
    >
      <button
        type="button"
        role="radio"
        aria-checked={!isListener}
        onClick={() => {
          onChange('psychologist');
        }}
        className={cn(
          'text-[14px] leading-5 transition-colors',
          isListener
            ? 'font-medium text-[var(--color-client-muted-foreground-soft)]'
            : 'font-semibold text-[var(--color-client-foreground)]',
        )}
      >
        Psychologist
      </button>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={() => {
          onChange(isListener ? 'psychologist' : 'volunteer');
        }}
        className={cn(
          'relative inline-flex h-5 w-[34px] shrink-0 items-center rounded-full',
          isListener
            ? 'bg-[var(--color-client-volunteer-primary)]'
            : 'bg-[var(--color-client-psych-primary)]',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'shadow-client-sm absolute top-[3px] left-[3px] h-3.5 w-3.5 rounded-full bg-white transition-transform',
            isListener && 'translate-x-3.5',
          )}
        />
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={isListener}
        onClick={() => {
          onChange('volunteer');
        }}
        className={cn(
          'text-[14px] leading-5 transition-colors',
          isListener
            ? 'font-semibold text-[var(--color-client-foreground)]'
            : 'font-medium text-[var(--color-client-muted-foreground-soft)]',
        )}
      >
        Listener
      </button>
    </div>
  );
}

function ClientBookPractitionerStep() {
  const { t } = useTranslation('clientBooking');
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [modeFilter, setModeFilter] = useState<'online' | 'in_person' | null>(null);
  const [concernFilter, setConcernFilter] = useState<PractitionerConcern[]>([]);
  const [languageFilter, setLanguageFilter] = useState<Language[]>([]);
  const [specializationFilter, setSpecializationFilter] = useState<PractitionerSpecialization[]>(
    [],
  );
  const [kindFilter, setKindFilter] = useState<PractitionerKind>('psychologist');
  const [searchQuery, setSearchQuery] = useState('');

  // The whole roster for the booking window, one request per kind. Everything
  // below (filters, search, sections, next opening) runs in memory, so nobody
  // drops out of the list just because they are off today.
  const psychRoster = useBookingRoster('psychologist');
  const volRoster = useBookingRoster('volunteer');
  const hasVolunteers = (volRoster.data?.results.length ?? 0) > 0;
  const kind: PractitionerKind = hasVolunteers ? kindFilter : 'psychologist';
  const copy = KIND_COPY_KEYS[kind];
  const activeRoster = kind === 'volunteer' ? volRoster : psychRoster;
  const isAnyLoading = psychRoster.isLoading || volRoster.isLoading;
  const durationMinutes = search.durationMinutes ?? DEFAULT_DURATION_MINUTES;

  const now = new Date();
  const rows: PickerRow[] = (activeRoster.data?.results ?? []).map((entry) => ({
    entry,
    nextDate: nextAvailableDate({ slots: entry.slots, durationMinutes, mode: modeFilter, now }),
  }));

  // Sections come from the whole roster, THEN filters and search narrow each
  // one, so narrowing never promotes a fourth-most-recent practitioner into
  // "Your ...". Recent keeps most-recent-first; everyone else is ordered by
  // their next opening.
  const { recent, others } = splitRecentPractitioners({
    items: rows,
    getLastSessionAt: (row) => row.entry.lastSessionAt,
  });
  const isShown = (row: PickerRow) =>
    matchesNameQuery({ name: rosterName(row.entry), query: searchQuery }) &&
    overlaps({ stored: row.entry.practitioner.concerns, wanted: concernFilter }) &&
    overlaps({ stored: row.entry.practitioner.languages, wanted: languageFilter }) &&
    overlaps({ stored: row.entry.practitioner.specializations, wanted: specializationFilter }) &&
    // A mode filter asks "who can I see online / in person this week", so
    // anyone with no opening in that mode drops out instead of showing as
    // unavailable.
    (modeFilter === null || row.nextDate !== null);
  const recentShown = recent.filter(isShown);
  const othersShown = others.filter(isShown).sort(compareByNextOpening);
  const isRosterEmpty = rows.length === 0;
  const hasActiveFilter =
    modeFilter !== null ||
    concernFilter.length > 0 ||
    languageFilter.length > 0 ||
    specializationFilter.length > 0 ||
    searchQuery.trim() !== '';
  const clearFilters = () => {
    setModeFilter(null);
    setConcernFilter([]);
    setLanguageFilter([]);
    setSpecializationFilter([]);
    setSearchQuery('');
  };

  const profileRow =
    profileId !== null ? (rows.find((r) => r.entry.practitioner.id === profileId) ?? null) : null;

  // Open the date/time step on the practitioner's first bookable day rather
  // than today, so the student lands on slots instead of an empty day.
  const onContinue = ({
    practitionerId,
    nextDate,
  }: {
    practitionerId: string;
    nextDate: string | null;
  }) => {
    void navigate({
      to: '/client/book/when',
      search: { ...search, practitionerId, date: nextDate ?? search.date },
    });
  };

  const renderGrid = (list: PickerRow[]) => (
    <div className="grid w-full grid-cols-1 items-start gap-3 sm:gap-2.5 md:grid-cols-2 lg:grid-cols-3">
      {list.map((row) => {
        const p = row.entry.practitioner;
        return (
          <ClientPractitionerCardLg
            key={p.id}
            practitioner={{
              ...p,
              bio: p.bio ?? null,
              practitionerKind: p.practitionerKind ?? 'psychologist',
            }}
            slotsAvailable={row.nextDate !== null}
            availableDayLabel={row.nextDate !== null ? formatRelativeDayLabel(row.nextDate) : null}
            unavailableLabel={t('wizard.step.practitioner.notAvailableThisWeek')}
            continueDisabled={row.nextDate === null}
            sessionModeLabel={sessionModeLabelFor(row.entry.slots)}
            onContinue={() => {
              onContinue({ practitionerId: p.id, nextDate: row.nextDate });
            }}
            expanded={expanded === p.id}
            onToggleExpanded={() => {
              setExpanded((prev) => (prev === p.id ? null : p.id));
            }}
            onViewProfile={() => {
              setProfileId(p.id);
            }}
          />
        );
      })}
    </div>
  );

  return (
    <ClientWizardLayout current={1} contentMaxWidth="wide">
      <div className="flex flex-col items-center gap-5 sm:gap-6">
        <h1 className="text-center text-[18px] leading-7 font-semibold text-[var(--color-client-primary-500)] sm:text-[20px] sm:leading-8">
          {t('wizard.step.practitioner.title')}
        </h1>

        {/* Mobile: stack filters vertically so each pill becomes a full-row
            tap target (uniform widths, no awkward centered narrow pills).
            sm+: wrap inline as before. */}
        <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-3">
          <ClientFilterPill
            label={t('wizard.step.practitioner.filterModes')}
            leadingIcon={<img src="/modes.svg" alt="" className="h-4 w-4" />}
            options={[
              { value: 'online', label: t('wizard.modeFilter.online') },
              { value: 'in_person', label: t('wizard.modeFilter.in_person') },
            ]}
            value={modeFilter}
            onChange={setModeFilter}
          />
          <ClientFilterPill
            label={t('wizard.step.practitioner.filterConcerns')}
            leadingIcon={<HandHeart className="h-4 w-4" strokeWidth={1.5} aria-hidden />}
            options={PRACTITIONER_CONCERN_KEYS.map((k) => ({ value: k, label: CONCERN_LABELS[k] }))}
            value={concernFilter}
            onChange={setConcernFilter}
            multiSelect
          />
          <ClientFilterPill
            label={t('wizard.step.practitioner.filterLanguages')}
            leadingIcon={<img src="/language.svg" alt="" className="h-4 w-4" />}
            options={LANGUAGE_KEYS.map((k) => ({ value: k, label: LANGUAGE_LABELS[k] }))}
            value={languageFilter}
            onChange={setLanguageFilter}
            multiSelect
          />
          <ClientFilterPill
            label={t('wizard.step.practitioner.filterSpecialization')}
            leadingIcon={<Sparkles className="h-4 w-4" strokeWidth={1.5} aria-hidden />}
            options={PRACTITIONER_SPECIALIZATION_KEYS.map((k) => ({
              value: k,
              label: SPECIALIZATION_LABELS[k],
            }))}
            value={specializationFilter}
            onChange={setSpecializationFilter}
            multiSelect
          />
          {hasVolunteers ? (
            <PractitionerKindToggle
              value={kindFilter}
              onChange={(next) => {
                setKindFilter(next);
                // A psychologist's name typed here means nothing in listeners.
                setSearchQuery('');
              }}
            />
          ) : null}
        </div>

        {!isAnyLoading && !isRosterEmpty ? (
          <div className="flex w-full justify-end">
            <ClientSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t(copy.searchPlaceholder)}
              clearLabel={t('wizard.step.practitioner.searchClear')}
              className="w-full sm:w-64"
            />
          </div>
        ) : null}

        {!isAnyLoading && recentShown.length === 0 && othersShown.length === 0 ? (
          <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-4 py-10 text-center sm:px-6 sm:py-12">
            <span
              aria-hidden
              className="inline-flex h-14 w-14 items-center justify-center rounded-full border-1 border-[var(--color-client-primary-500)] bg-[var(--color-client-primary-tint-soft)] text-[var(--color-client-primary-500)]"
            >
              <UserRoundSearch className="h-6 w-6" strokeWidth={1.5} />
            </span>
            <div className="flex flex-col gap-1.5">
              <h3 className="text-[16px] leading-6 font-semibold text-[var(--color-client-foreground)]">
                {isRosterEmpty
                  ? t('wizard.step.practitioner.rosterEmptyHeadline')
                  : t('wizard.step.practitioner.emptyHeadline')}
              </h3>
              <p className="text-[14px] leading-5 text-[var(--color-client-muted-foreground-soft)]">
                {isRosterEmpty
                  ? t('wizard.step.practitioner.rosterEmptyDescription')
                  : t('wizard.step.practitioner.emptyDescription')}
              </p>
            </div>
            {hasActiveFilter && !isRosterEmpty ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-[var(--color-client-primary-tint-soft)] text-[var(--color-client-primary-500)] hover:bg-[rgba(var(--color-client-primary-500-rgb),0.05)]"
                onClick={clearFilters}
              >
                {t('wizard.step.practitioner.emptyClearAll')}
              </Button>
            ) : null}
          </div>
        ) : null}

        {/* Placeholder cards at the real card's height keep the page from
            collapsing and then springing back when the roster lands. */}
        {isAnyLoading ? (
          <div
            data-testid="practitioner-grid-loading"
            className="grid w-full grid-cols-1 items-start gap-3 sm:gap-2.5 md:grid-cols-2 lg:grid-cols-3"
          >
            {Array.from({ length: PRACTITIONER_SKELETON_COUNT }, (_, i) => (
              <ClientListCardSkeleton key={i} media="avatar" lines={2} />
            ))}
          </div>
        ) : recent.length === 0 ? (
          // No past sessions: one plain grid, as before this feature.
          renderGrid(othersShown)
        ) : (
          <div className="flex w-full flex-col gap-6">
            {recentShown.length > 0 ? (
              <ClientPickerSection title={t(copy.recent)}>
                {renderGrid(recentShown)}
              </ClientPickerSection>
            ) : null}
            {othersShown.length > 0 ? (
              <ClientPickerSection title={t(copy.other)}>
                {renderGrid(othersShown)}
              </ClientPickerSection>
            ) : null}
          </div>
        )}
      </div>

      <PractitionerProfileDialog
        open={profileId !== null}
        onOpenChange={(next) => {
          if (!next) setProfileId(null);
        }}
        practitioner={
          profileRow
            ? {
                ...profileRow.entry.practitioner,
                bio: profileRow.entry.practitioner.bio ?? null,
                practitionerKind: profileRow.entry.practitioner.practitionerKind ?? 'psychologist',
              }
            : null
        }
        gender={profileRow ? (profileRow.entry.practitioner.gender ?? null) : null}
        sessionModeLabel={profileRow ? sessionModeLabelFor(profileRow.entry.slots) : null}
        bookSessionDisabled={(profileRow?.nextDate ?? null) === null}
        onBookSession={() => {
          if (profileRow !== null && profileRow.nextDate !== null) {
            onContinue({
              practitionerId: profileRow.entry.practitioner.id,
              nextDate: profileRow.nextDate,
            });
          }
          setProfileId(null);
        }}
      />
    </ClientWizardLayout>
  );
}
